import * as React from 'react';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import {
  CCM_REPORTING_METRICS_COLUMNS,
  CCM_REPORTING_METRICS_TO_OUTFIT_PERFORMANCE_MAP
} from '../utils/ccm-campaign-reporting';

import './ccm-campaign-reporting-metrics.scss';

applyTranslations(require('./ccm-campaign-reporting-metrics.i18n.json'));

import type {
  CcmCampaignReportingPerformanceSummary
} from 'modules/campaigns/components/ccm/types/CcmCampaignReporting.type';

type Props = {
  summary: CcmCampaignReportingPerformanceSummary | undefined,
  shouldHaveData: boolean
};

const MSG_KEY = 'ccm_campaign_reporting_metrics';

const CcmCampaignReportingMetrics: React.FC<Props> = ({
  summary,
  shouldHaveData
}) => {
  if (isNil(summary)) {
    return null;
  }

  const getLastUpdatedSubtext = (): React.ReactNode => {
    const dataUpdateProgress = summary.dataUpdateProgress;

    if (dataUpdateProgress === 'none') {
      return __t(`${MSG_KEY}.last_updated.not_live`);
    }

    if (dataUpdateProgress === 'stale') {
      return (
        <span>
          {__t(`${MSG_KEY}.last_updated.being_calculated`)}
        </span>
      );
    }

    if (dataUpdateProgress === 'up_to_date' || dataUpdateProgress === 'no_longer_tracked') {
      return (
        <span>
          {__t(`${MSG_KEY}.last_updated.current`)}
        </span>
      );
    }

    return '';
  };

  const renderLastUpdated = (): React.ReactNode => {
    const subtext = getLastUpdatedSubtext();

    return (
      <div className="ccm-campaign-reporting-metrics__last-updated">
        {
          shouldHaveData && summary.dataUpdateProgress !== 'none' && (
            <React.Fragment>
              {`${__t(`${MSG_KEY}.last_updated.data`)}: ${getDisplayDate(summary.dataLastUpdated)}.`}
              &nbsp;
            </React.Fragment>
          )
        }
        {subtext}
      </div>
    );
  };

  const renderSection = (
    key: string,
    index: number
  ): React.ReactNode => {
    const valueMap = CCM_REPORTING_METRICS_TO_OUTFIT_PERFORMANCE_MAP[key];
    const value = get(summary, valueMap.propName, valueMap.defaultValue);

    return (
      <React.Fragment key={`ccm-campaign-reporting-metrics--${key}`}>
        <div className="ccm-campaign-reporting-metrics__section">
          <div className="ccm-campaign-reporting-metrics__section-title">
            {valueMap.formatter(value)}
          </div>
          <div className="ccm-campaign-reporting-metrics__section-label">
            {__t(`${MSG_KEY}.labels.${key}`)}
          </div>
        </div>
        {
          index < CCM_REPORTING_METRICS_COLUMNS.length - 1 && (
            <div className="ccm-campaign-reporting-metrics__section-divider" />
          )
        }
      </React.Fragment>
    );
  };

  return (
    <div className="ccm-campaign-reporting-metrics">
      <div className="ccm-campaign-reporting-metrics__sections">
        {CCM_REPORTING_METRICS_COLUMNS.map(renderSection)}
      </div>
      {renderLastUpdated()}
    </div>
  );
};

export default CcmCampaignReportingMetrics;
